import React, { useState, useEffect } from 'react';
import FirebaseService from '../../services/FirebaseService';

const ReusableAddOrganizationForm = ({ onSuccess, organizationToEdit, onCancel }) => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  // Initialize form with default values
  const [formData, setFormData] = useState({
    org_name: '',
    certi_no: '',
    city: '',
    state: '',
    country: '',
    program: '',
    issue_date: '',
    exp_date: '',
    status: 'ACCREDITED',
    notes: ''
  });
  
  // Format date for HTML date input (YYYY-MM-DD)
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    try {
      // Handle different date formats
      let date;
      
      if (typeof dateString === 'string') {
        // Handle DD/MM/YYYY format (with slashes)
        if (dateString.includes('/')) {
          const parts = dateString.split('/');
          // Make sure we have day, month, year
          if (parts.length === 3) {
            // Create date in YYYY-MM-DD format
            date = new Date(`${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`);
          } else {
            // If not in expected format, try direct parsing
            date = new Date(dateString);
          }
        }
        // Handle DD-MM-YYYY format (with dashes)
        else if (dateString.includes('-') && dateString.split('-').length === 3) {
          const parts = dateString.split('-');
          // Check if it's DD-MM-YYYY (where first part is likely 2 digits for day)
          if (parts[0].length === 2 && !isNaN(parseInt(parts[0]))) {
            // Create date in YYYY-MM-DD format
            date = new Date(`${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`);
          } else {
            // Likely already in YYYY-MM-DD format
            date = new Date(dateString);
          }
        }
        // Handle other formats
        else {
          date = new Date(dateString);
        }
      } else {
        // If not a string, try direct parsing
        date = new Date(dateString);
      }
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        // Removed console.log
        return ''; // Return empty string for invalid date
      }
      
      // Format as YYYY-MM-DD for HTML date input
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (error) {
      // Removed console.error
      return '';
    }
  };

  // Populate form with organization data if editing
  useEffect(() => {
    if (organizationToEdit) {
      // Removed console.log
      
      setIsEditing(true);
      setFormData({
        org_name: organizationToEdit.org_name || '',
        certi_no: organizationToEdit.certi_no || '',
        city: organizationToEdit.city || '',
        state: organizationToEdit.state || '',
        country: organizationToEdit.country || '',
        program: organizationToEdit.program || '',
        issue_date: formatDateForInput(organizationToEdit.issue_date),
        exp_date: formatDateForInput(organizationToEdit.exp_date),
        status: typeof organizationToEdit.status === 'boolean' 
          ? (organizationToEdit.status ? 'ACCREDITED' : 'INACTIVE')
          : organizationToEdit.status || 'ACCREDITED',
        notes: organizationToEdit.notes || ''
      });
      
      // Log for debugging
      // Removed console.log
      console.log('Formatted dates:', {
        issue_date: formatDateForInput(organizationToEdit.issue_date),
        exp_date: formatDateForInput(organizationToEdit.exp_date)
      });
    }
  }, [organizationToEdit]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const validateForm = () => {
    const requiredFields = [
      'org_name',
      'certi_no',
      'country',
      'program',
      'issue_date',
      'exp_date'
    ];

    for (const field of requiredFields) {
      if (!formData[field]) {
        setError(
          `${field.replace('_', ' ').charAt(0).toUpperCase() + field.replace('_', ' ').slice(1)} is required`
        );
        return false;
      }
    }
    setError(''); // Clear error if all fields are valid
    return true;
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
    setIsEditing(false);
    setFormData({
      org_name: '',
      certi_no: '',
      city: '',
      state: '',
      country: '',
      program: '',
      issue_date: '',
      exp_date: '',
      status: 'active',
      notes: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      // Map form fields to match Firestore field names
      const formattedData = {
        org_name: formData.org_name,
        certi_no: formData.certi_no,
        city: formData.city,
        state: formData.state,
        country: formData.country,
        program: formData.program,
        issue_date: formData.issue_date ? new Date(formData.issue_date).toISOString() : null,
        exp_date: formData.exp_date ? new Date(formData.exp_date).toISOString() : null,
        status: formData.status, // Keep as string
        notes: formData.notes,
        updated_at: new Date().toISOString()
      };
      
      // Removed console.log

      if (isEditing && organizationToEdit?.id) {
        // Update existing organization
        await FirebaseService.updateAccreditedOrganization(organizationToEdit.id, formattedData);
        setSuccess(true);
        setIsEditing(false);
      } else {
        // Add new organization
        formattedData.created_at = new Date().toISOString();
        await FirebaseService.addAccreditedOrganization(formattedData);
        setSuccess(true);
      }

      // Reset form after successful submission
      setFormData({
        org_name: '',
        certi_no: '',
        city: '',
        state: '',
        country: '',
        program: '',
        issue_date: '',
        exp_date: '',
        status: 'ACCREDITED',
        notes: ''
      });

      if (onSuccess) {
        // Call the onSuccess callback after a delay to allow the user to see the success message
        setTimeout(() => {
          onSuccess();
          setSuccess(false); // Reset success message after callback
        }, 1500);
      }
    } catch (err) {
      // Removed console.error
      setError(err.message || 'Failed to save organization. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%',
    height: '44px',
    border: '1.5px solid #e2e8f0',
    borderRadius: '8px',
    padding: '0 14px',
    fontSize: '14px',
    color: '#0a1628',
    backgroundColor: '#f8fafc',
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    transition: 'border-color 0.2s, box-shadow 0.2s',
  };

  const labelStyle = {
    display: 'block',
    fontSize: '12px',
    fontWeight: '600',
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '6px',
  };

  const fieldWrap = { display: 'flex', flexDirection: 'column' };

  const sectionLabel = {
    fontSize: '11px',
    fontWeight: '700',
    color: '#94a3b8',
    textTransform: 'uppercase',
    letterSpacing: '0.8px',
    marginBottom: '16px',
    paddingBottom: '8px',
    borderBottom: '1px solid #f1f5f9',
  };

  const handleFocus = (e) => {
    e.target.style.borderColor = '#0d2347';
    e.target.style.boxShadow = '0 0 0 3px rgba(13,35,71,0.08)';
    e.target.style.backgroundColor = '#ffffff';
  };
  const handleBlur = (e) => {
    e.target.style.borderColor = '#e2e8f0';
    e.target.style.boxShadow = 'none';
    e.target.style.backgroundColor = '#f8fafc';
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      {error && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '10px',
          backgroundColor: '#fef2f2', border: '1px solid #fecaca',
          borderRadius: '8px', padding: '12px 16px', marginBottom: '24px',
          fontSize: '13.5px', color: '#b91c1c',
        }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          {error}
        </div>
      )}

      {success && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '10px',
          backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0',
          borderRadius: '8px', padding: '12px 16px', marginBottom: '24px',
          fontSize: '13.5px', color: '#15803d',
        }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
          </svg>
          Organization {isEditing ? 'updated' : 'added'} successfully!
        </div>
      )}

      {/* Section 1: Identity */}
      <div style={{ marginBottom: '28px' }}>
        <div style={sectionLabel}>Organization Identity</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px 24px' }}>
          <div style={{ ...fieldWrap, gridColumn: '1 / -1' }}>
            <label style={labelStyle}>Organization Name <span style={{ color: '#ef4444' }}>*</span></label>
            <input
              name="org_name" value={formData.org_name} onChange={handleChange}
              onFocus={handleFocus} onBlur={handleBlur}
              placeholder="Full legal name of the organization"
              style={inputStyle}
            />
          </div>
          <div style={fieldWrap}>
            <label style={labelStyle}>Certificate Number <span style={{ color: '#ef4444' }}>*</span></label>
            <input
              name="certi_no" value={formData.certi_no} onChange={handleChange}
              onFocus={handleFocus} onBlur={handleBlur}
              placeholder="e.g. EUAS/IN/ACCP/4864/0137"
              style={{ ...inputStyle, fontFamily: 'monospace', fontSize: '13px' }}
            />
          </div>
          <div style={fieldWrap}>
            <label style={labelStyle}>Program <span style={{ color: '#ef4444' }}>*</span></label>
            <input
              name="program" value={formData.program} onChange={handleChange}
              onFocus={handleFocus} onBlur={handleBlur}
              placeholder="e.g. ISO/IEC 17021-1:2015"
              style={inputStyle}
            />
          </div>
        </div>
      </div>

      {/* Section 2: Location */}
      <div style={{ marginBottom: '28px' }}>
        <div style={sectionLabel}>Location</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px 24px' }}>
          <div style={fieldWrap}>
            <label style={labelStyle}>City</label>
            <input
              name="city" value={formData.city} onChange={handleChange}
              onFocus={handleFocus} onBlur={handleBlur}
              placeholder="City"
              style={inputStyle}
            />
          </div>
          <div style={fieldWrap}>
            <label style={labelStyle}>State / Province</label>
            <input
              name="state" value={formData.state} onChange={handleChange}
              onFocus={handleFocus} onBlur={handleBlur}
              placeholder="State or Province"
              style={inputStyle}
            />
          </div>
          <div style={fieldWrap}>
            <label style={labelStyle}>Country <span style={{ color: '#ef4444' }}>*</span></label>
            <input
              name="country" value={formData.country} onChange={handleChange}
              onFocus={handleFocus} onBlur={handleBlur}
              placeholder="Country"
              style={inputStyle}
            />
          </div>
        </div>
      </div>

      {/* Section 3: Certification Details */}
      <div style={{ marginBottom: '28px' }}>
        <div style={sectionLabel}>Certification Details</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px 24px' }}>
          <div style={fieldWrap}>
            <label style={labelStyle}>Issue Date <span style={{ color: '#ef4444' }}>*</span></label>
            <input
              type="date" name="issue_date" value={formData.issue_date} onChange={handleChange}
              onFocus={handleFocus} onBlur={handleBlur}
              style={inputStyle}
            />
          </div>
          <div style={fieldWrap}>
            <label style={labelStyle}>Expiration Date <span style={{ color: '#ef4444' }}>*</span></label>
            <input
              type="date" name="exp_date" value={formData.exp_date} onChange={handleChange}
              onFocus={handleFocus} onBlur={handleBlur}
              style={inputStyle}
            />
          </div>
          <div style={fieldWrap}>
            <label style={labelStyle}>Status</label>
            <select
              name="status" value={formData.status} onChange={handleChange}
              onFocus={handleFocus} onBlur={handleBlur}
              style={{ ...inputStyle, cursor: 'pointer', appearance: 'auto' }}
            >
              <option value="ACCREDITED">ACCREDITED</option>
              <option value="INACTIVE">INACTIVE</option>
              <option value="SUSPENDED">SUSPENDED</option>
              <option value="EXPIRED">EXPIRED</option>
            </select>
          </div>
          <div style={{ ...fieldWrap, gridColumn: '1 / -1' }}>
            <label style={labelStyle}>Notes <span style={{ color: '#94a3b8', fontWeight: '400', textTransform: 'none', letterSpacing: 0 }}>(Optional)</span></label>
            <textarea
              name="notes" value={formData.notes} onChange={handleChange}
              onFocus={handleFocus} onBlur={handleBlur}
              placeholder="Additional notes about the organization..."
              rows={3}
              style={{
                ...inputStyle, height: 'auto', padding: '10px 14px',
                resize: 'vertical', lineHeight: '1.5',
              }}
            />
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div style={{
        display: 'flex', justifyContent: 'flex-end', gap: '12px',
        paddingTop: '20px', borderTop: '1px solid #f1f5f9',
      }}>
        {isEditing && (
          <button
            type="button" onClick={handleCancel} disabled={loading}
            style={{
              height: '42px', padding: '0 20px', border: '1.5px solid #e2e8f0',
              borderRadius: '8px', background: '#ffffff', color: '#64748b',
              fontSize: '13.5px', fontWeight: '500', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: '6px', fontFamily: 'inherit',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
            Cancel
          </button>
        )}
        <button
          type="submit" disabled={loading}
          style={{
            height: '42px', padding: '0 24px',
            background: loading ? '#94a3b8' : 'linear-gradient(135deg, #0d2347 0%, #1a3a6e 100%)',
            color: '#ffffff', border: 'none', borderRadius: '8px',
            fontSize: '13.5px', fontWeight: '600', cursor: loading ? 'not-allowed' : 'pointer',
            display: 'flex', alignItems: 'center', gap: '8px', fontFamily: 'inherit',
            boxShadow: loading ? 'none' : '0 4px 12px rgba(13,35,71,0.25)',
            transition: 'all 0.2s',
          }}
        >
          {loading ? (
            <>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ animation: 'spin 1s linear infinite' }}>
                <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
              </svg>
              Saving...
            </>
          ) : (
            <>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/>
              </svg>
              {isEditing ? 'Update Organization' : 'Add Organization'}
            </>
          )}
        </button>
      </div>
    </form>
  );
};

export default ReusableAddOrganizationForm;
